import type { GameId, HistoryEntry } from '@xox/shared'
import { useFetch } from '../hooks/useFetch'
import { api } from '../lib/api'
import { GAMES } from '../lib/games'
import { Card } from './ui'

const REASON: Record<GameId, Record<HistoryEntry['endReason'], string>> = {
  xox: { line: 'three in a row', timeout: 'on time', abandon: 'by forfeit', draw: 'by repetition' },
  c4: { line: 'four in a row', timeout: 'on time', abandon: 'by forfeit', draw: 'board full' },
}

const gameTitle = (game: GameId) => GAMES.find((g) => g.to === `/${game}`)?.title ?? game
/** How each game names its two seats. */
const SEAT_NAME: Record<GameId, Record<HistoryEntry['youWere'], string>> = {
  xox: { X: 'X', O: 'O' },
  c4: { X: 'red', O: 'yellow' },
}

/** The player's finished duels, newest first. */
export function HistoryList() {
  const { data: rows, error } = useFetch(api.history)
  return (
    <>
      {error && <p className="text-rose-600">{error}</p>}
      {rows && rows.length === 0 && (
        <Card>
          <p className="text-slate-500">You haven't finished a game yet.</p>
        </Card>
      )}
      <ul className="flex flex-col gap-2">
        {rows?.map((g) => (
          <li key={g.id}>
            <Card className="flex items-center justify-between gap-3 py-3">
              <div className="flex items-center gap-3">
                <span
                  className={[
                    'inline-flex w-14 justify-center rounded-full px-2 py-0.5 text-xs font-bold uppercase',
                    g.result === 'win'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                      : g.result === 'draw'
                        ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                        : 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
                  ].join(' ')}
                >
                  {g.result}
                </span>
                <div>
                  <div className="font-medium">
                    vs {g.opponent?.nickname ?? 'unknown'}{' '}
                    <span className="text-xs text-slate-500">
                      as {SEAT_NAME[g.game][g.youWere]}
                    </span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    <span className="whitespace-nowrap rounded-full bg-slate-200 px-1.5 text-[10px] font-bold uppercase text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                      {gameTitle(g.game)}
                    </span>
                    {g.mode === 'cpu' && (
                      <span className="whitespace-nowrap rounded-full bg-violet-100 px-1.5 text-[10px] font-bold uppercase text-violet-700 dark:bg-violet-950/60 dark:text-violet-300">
                        computer
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {REASON[g.game][g.endReason]} · {g.moveCount} moves
                  </div>
                </div>
              </div>
              <time className="text-xs text-slate-500" dateTime={g.endedAt}>
                {new Date(g.endedAt).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </time>
            </Card>
          </li>
        ))}
      </ul>
    </>
  )
}
